// 안드로이드 TV 감지
export const isAndroidTV = () => {
  const userAgent = navigator.userAgent || "";
  return (
    userAgent.indexOf("Android") !== -1 &&
    (userAgent.indexOf("TV") !== -1 || userAgent.indexOf("BRAVIA") !== -1)
  );
};

// BrowseHere 브라우저 감지
export const isBrowseHere = () => {
  const userAgent = navigator.userAgent || "";
  return (
    userAgent.indexOf("BrowseHere") !== -1 ||
    userAgent.indexOf("browsehere") !== -1
  );
};

// 키오스크 환경 감지
export const isKioskEnvironment = () => {
  const userAgent = navigator.userAgent || "";

  // BrowseHere 브라우저 감지
  if (isBrowseHere()) {
    return true;
  }

  // Fully-Kiosk 브라우저 감지
  if (
    userAgent.indexOf("Fully") !== -1 ||
    userAgent.indexOf("FullyBrowser") !== -1
  ) {
    return true;
  }

  // Fully-Kiosk 브라우저의 특별한 객체 확인
  if (typeof window.fully !== "undefined") {
    return true;
  }

  // 기타 키오스크 앱들
  if (userAgent.indexOf("Kiosk") !== -1) {
    return true;
  }

  // 안드로이드 TV에서 standalone 모드
  if (window.navigator.standalone) {
    return true;
  }

  // fullscreen API 사용 중
  if (document.fullscreenElement || document.webkitFullscreenElement) {
    return true;
  }

  // URL 파라미터로 강제 키오스크 모드 (테스트용)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("kiosk") === "true") {
    return true;
  }

  return false;
};

// URL 파라미터 가져오기
export const getUrlParameter = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

// 🎯 업데이트된 함수: TV ID를 URL에서 가져오기 (새로운 /{tvName} 방식 + 하위 호환성)
export const getTVIdFromUrl = () => {
  const pathSegments = window.location.pathname
    .split("/")
    .filter((segment) => segment);

  // 1. 새로운 동적 라우팅 방식 확인 (/{tvName})
  if (pathSegments.length === 1) {
    const tvName = pathSegments[0];
    // 고정 경로들은 제외 (admin, login 등)
    const reservedPaths = ["admin", "login", "tv"];
    if (!reservedPaths.includes(tvName)) {
      log(`TV ID 추출 (루트 동적 라우팅): ${tvName}`);
      return tvName;
    }
  }

  // 2. 기존 /tv/tvName 방식 확인 (하위 호환성)
  if (pathSegments.length >= 2 && pathSegments[0] === "tv" && pathSegments[1]) {
    const tvName = pathSegments[1];
    log(`TV ID 추출 (기존 /tv 동적 라우팅): ${tvName}`);
    return tvName;
  }

  // 3. 기존 쿼리 파라미터 방식 확인 (/tv?id=tvName)
  const queryId = getUrlParameter("id");
  if (queryId) {
    log(`TV ID 추출 (쿼리 파라미터): ${queryId}`);
    return queryId;
  }

  // 4. 모두 없으면 null 반환
  log("TV ID를 찾을 수 없음");
  return null;
};

// 🎯 업데이트된 함수: TV URL 생성 (새로운 /{tvName} 방식)
export const createTVUrl = (tvName, baseUrl = window.location.origin) => {
  return `${baseUrl}/${tvName}`;
};

// 🎯 TV 이름 유효성 검사 (기존 함수 유지)
export const isValidTVName = (tvName) => {
  if (!tvName || typeof tvName !== "string") return false;

  // URL에 안전한 문자만 허용 (영문, 숫자, 하이픈, 언더스코어)
  const validPattern = /^[a-zA-Z0-9-_]+$/;
  return validPattern.test(tvName) && tvName.length > 0 && tvName.length <= 50;
};

// 🎯 추가 함수: 예약된 경로 체크
export const isReservedPath = (path) => {
  const reservedPaths = ["admin", "login", "tv"];
  return reservedPaths.includes(path);
};

// 로그 함수
export const log = (message, type = "info") => {
  const timestamp = new Date().toLocaleTimeString();
  // Logging disabled for production
};

// 성능 모니터링
export const logPerformance = (label, startTime) => {
  if (performance && performance.now) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    // Performance logging disabled
  }
};

// 에러 핸들링
export const handleError = (error, context = "") => {
  const errorMessage = error.message || error.toString();
  const fullMessage = context ? `${context}: ${errorMessage}` : errorMessage;

  // Error logging disabled
};

// 키보드 이벤트 유틸리티
export const getKeyInfo = (event) => {
  const keyCode = event.keyCode || event.which;
  const key = event.key;

  // 안드로이드 TV 리모컨 키 매핑
  const tvKeyMap = {
    37: "DPAD_LEFT",
    39: "DPAD_RIGHT",
    38: "DPAD_UP",
    40: "DPAD_DOWN",
    13: "DPAD_CENTER",
    23: "DPAD_CENTER", // 일부 TV에서
    21: "DPAD_LEFT", // 일부 TV에서
    22: "DPAD_RIGHT", // 일부 TV에서
    19: "DPAD_UP", // 일부 TV에서
    20: "DPAD_DOWN", // 일부 TV에서
    85: "MEDIA_PLAY_PAUSE",
    32: "SPACE",
  };

  return {
    keyCode,
    key,
    tvKey: tvKeyMap[keyCode] || null,
    isArrowKey: [37, 38, 39, 40].includes(keyCode),
    isEnterKey: [13, 23].includes(keyCode),
    isSpaceKey: keyCode === 32,
  };
};

// 사용자 상호작용 감지
export const createInteractionHandler = (callback) => {
  const events = ["click", "keydown", "touchstart", "focus"];

  const handler = (event) => {
    callback(event);
    // 이벤트 리스너 제거
    events.forEach((eventName) => {
      document.removeEventListener(eventName, handler);
    });
    window.removeEventListener("focus", handler);
  };

  // 이벤트 리스너 등록
  events.forEach((eventName) => {
    document.addEventListener(eventName, handler, { once: true });
  });
  window.addEventListener("focus", handler, { once: true });

  return handler;
};

// 로컬 스토리지 유틸리티
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },

  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      return false;
    }
  },
};

// 세션 스토리지 유틸리티
export const sessionStorage = {
  get: (key, defaultValue = null) => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error("sessionStorage get error:", error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error("sessionStorage set error:", error);
      return false;
    }
  },

  remove: (key) => {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error("sessionStorage remove error:", error);
      return false;
    }
  },
};

// 현재 시간 가져오기
export const getCurrentTime = () => {
  return new Date().toISOString();
};

// 랜덤 ID 생성
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// 클립보드에 복사
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // 폴백 방법
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    }
  } catch (error) {
    console.error("클립보드 복사 실패:", error);
    return false;
  }
};

// 전체화면 토글
export const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch((err) => {
      console.error("전체화면 진입 실패:", err);
    });
  } else {
    document.exitFullscreen().catch((err) => {
      console.error("전체화면 종료 실패:", err);
    });
  }
};

// 화면 잠금 방지 (웹 API 사용)
export const preventScreenLock = () => {
  if ("wakeLock" in navigator) {
    return navigator.wakeLock.request("screen").catch((err) => {
      console.error("화면 잠금 방지 실패:", err);
    });
  } else {
    console.warn("Wake Lock API가 지원되지 않습니다.");
    return Promise.reject(new Error("Wake Lock API not supported"));
  }
};
